import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag, Trash2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { useCart } from '../../store/cartContext';
import { DeliveryNotice } from './DeliveryNotice';

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    setCartOpen,
    closeCart,
    removeItem,
    updateQuantity,
    totalPrice,
    shipping,
    grandTotal
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 bg-card">
        <SheetHeader className="px-5 py-4 border-b border-border">
          <SheetTitle className="font-serif text-lg text-foreground flex items-center gap-2">
            <ShoppingBag size={20} className="text-primary" />
            Vaša Korpa
            {items.length > 0 && (
              <span className="ml-auto text-sm font-ui text-muted-foreground">
                ({items.length} {items.length === 1 ? 'stavka' : 'stavki'})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
              <ShoppingBag size={48} className="text-muted-foreground/30 mb-4" />
              <p className="font-serif text-lg text-foreground mb-2">Korpa je prazna</p>
              <p className="text-sm text-muted-foreground font-ui mb-6">
                Dodajte neke od naših domaće proizvoda
              </p>
              <Button
                onClick={() => { closeCart(); navigate('/proizvodi'); }}
                className="bg-primary text-primary-foreground font-ui"
              >
                Pogledaj proizvode
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map(item => (
                <div key={item.product.id} className="flex gap-3 p-4">
                  <img
                    src={item.product.primaryImageUrl}
                    alt={item.product.name}
                    className="h-16 w-16 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm font-medium text-foreground leading-snug line-clamp-1">
                      {item.product.name}
                    </p>
                    <p className="text-xs text-muted-foreground font-ui mt-0.5">
                      {item.product.price.toLocaleString('sr-RS')} RSD / {item.product.unit}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      {/* Qty controls */}
                      <div className="flex items-center gap-1 border border-border rounded-lg overflow-hidden">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="p-1.5 hover:bg-muted transition-colors"
                        >
                          <Minus size={12} className="text-foreground" />
                        </button>
                        <span className="px-2 text-sm font-ui font-medium text-foreground min-w-[2rem] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="p-1.5 hover:bg-muted transition-colors"
                        >
                          <Plus size={12} className="text-foreground" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="font-ui font-semibold text-sm text-foreground">
                          {(item.product.price * item.quantity).toLocaleString('sr-RS')} RSD
                        </span>
                        <button
                          onClick={() => removeItem(item.product.id)}
                          className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-4 bg-card">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-ui">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">{totalPrice.toLocaleString('sr-RS')} RSD</span>
              </div>
              <div className="flex justify-between text-sm font-ui">
                <span className="text-muted-foreground">Dostava</span>
                <span className={shipping === 0 ? 'text-primary font-medium' : 'text-foreground'}>
                  {shipping === 0 ? 'BESPLATNO' : `${shipping} RSD`}
                </span>
              </div>
              {shipping > 0 && <DeliveryNotice variant="inline" className="mt-1" />}
              <Separator />
              <div className="flex justify-between font-semibold font-ui">
                <span className="text-foreground">Ukupno</span>
                <span className="text-primary text-lg">{grandTotal.toLocaleString('sr-RS')} RSD</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleCheckout}
                className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui shadow-btn"
              >
                Nastavi na placanje
              </Button>
              <Button
                variant="ghost"
                onClick={() => { closeCart(); navigate('/korpa'); }}
                className="w-full font-ui text-muted-foreground hover:text-foreground"
              >
                Pregledaj korpu
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
